import { useEffect, useMemo, useRef, useState } from "react";
import { MOODS, SOCIAL_OPTIONS, moodFromSlider } from "../../../shared/moods";
import {
  placesForPlan,
  PLACE_KIND_LABELS,
  type SettingPref,
} from "../../../shared/plans";
import { computeRadioMaximoKm, engineRadii, rankedItemsToPlanCards, searchPlansEngine } from "../../../shared/planEngine";
import { recommendPlansSmartDetailed } from "../../../shared/plansAi";
import { fitsBudget, fitsTime, formatBudget, formatMinutes, parseBudget, parseFreeTime, planFiltersKey } from "../../../shared/planFilters";
import { PlanLimitsInputs, type PlanLimitsValue } from "../components/PlanLimitsInputs";
import {
  coordsMovedMeters,
  geocodeAddress,
  type Coords,
} from "../../../shared/geo";
import {
  fetchNearbyPlacesCascade,
  fetchWeather,
  googleMapsLoop,
  leafletMapHtml,
  leafletPinPickerHtml,
  planMapsUrl,
  type TravelMode,
} from "../../../shared/places";
import type { NearbyPlace, SocialContext } from "../../../shared/types";
import { useLiveLocation } from "../lib/useLiveLocation";
import { useCategories } from "../lib/useCategories";
import { CategoryFilter } from "../components/CategoryFilter";
import { categoryById, countByCategory, describeCategories, filterPlacesByCategories } from "../../../shared/categories";
import { readKit, readSavedPlans, savePlans, type SavedPlan } from "../lib/kv";

type Plan = ReturnType<typeof rankedItemsToPlanCards>[number];

function safeJson(raw: string): { type?: string; latitude?: number; longitude?: number } | null {
  try {
    return JSON.parse(raw) as { type?: string; latitude?: number; longitude?: number };
  } catch {
    return null;
  }
}

const MODES: { key: TravelMode; label: string }[] = [
  { key: "walk", label: "A pie" },
  { key: "run", label: "Corriendo" },
  { key: "bike", label: "En bici" },
  { key: "car", label: "En coche" },
];

export default function SearchActivities() {
  const { coords, error: gpsError, ready: gpsReady } = useLiveLocation();
  const [mood, setMood] = useState(0.52);
  const [social, setSocial] = useState<SocialContext>("solo");
  const [setting, setSetting] = useState<SettingPref>("any");
  const [categories, setCategories] = useCategories();
  const [limits, setLimits] = useState<PlanLimitsValue>({ hours: "1", minutes: "30", budget: "" });
  const [mode, setMode] = useState<TravelMode>("walk");
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [savedPlans, setSavedPlans] = useState<SavedPlan[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [weatherLine, setWeatherLine] = useState("");
  const [places, setPlaces] = useState<NearbyPlace[]>([]);
  const [radiusUsed, setRadiusUsed] = useState(0);
  const [sourceNote, setSourceNote] = useState("Activando GPS en vivo…");
  const [origin, setOrigin] = useState<Coords | null>(null);
  const [locationMode, setLocationMode] = useState<"gps" | "manual">("gps");
  const [address, setAddress] = useState({ country: "", region: "", city: "", postal: "", street: "", number: "" });
  const [geoNote, setGeoNote] = useState("");
  const [routeNote, setRouteNote] = useState("");
  const [saveNote, setSaveNote] = useState("");
  const [formError, setFormError] = useState("");
  const [aiNote, setAiNote] = useState("");
  const [lastSearchKey, setLastSearchKey] = useState<string | null>(null);
  const lastFetch = useRef<{ latitude: number; longitude: number; radius: number; places: NearbyPlace[] } | null>(null);
  const runGen = useRef(0);
  const time = parseFreeTime(limits.hours, limits.minutes);
  const budget = parseBudget(limits.budget);
  const currentKey = planFiltersKey({
    minutes: time.ok ? time.minutes : null,
    budget: budget.ok ? budget.budget : "invalid",
    mode,
    setting,
    social,
    mood,
    categories,
    locationKey: locationMode === "manual" && origin ? `${origin.latitude.toFixed(4)},${origin.longitude.toFixed(4)}` : locationMode,
  });
  const filtersChanged = lastSearchKey != null && lastSearchKey !== currentKey;

  useEffect(() => {
    void readSavedPlans().then(setSavedPlans);
  }, []);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const data = typeof event.data === "string" ? safeJson(event.data) : event.data;
      if (!data || data.type !== "hermes-pin") return;
      const latitude = Number(data.latitude);
      const longitude = Number(data.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
      setLocationMode("manual");
      setOrigin({ latitude, longitude, source: "network", accuracyMeters: 30 });
      setGeoNote("Pin movido en el mapa.");
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Solo situamos el punto de partida: NO se busca nada hasta pulsar «Buscar planes».
  useEffect(() => {
    // Tras una búsqueda el texto de estado muestra el resumen de resultados: no lo pisamos con cada lectura del GPS.
    const idle = lastSearchKey == null;
    if (locationMode === "manual") {
      if (idle) {
        setSourceNote(
          origin
            ? "Ubicación manual lista. Ajusta los filtros y pulsa «Buscar planes»."
            : "Escribe la dirección completa o mueve el pin.",
        );
      }
      return;
    }
    if (!coords) {
      if (gpsReady && gpsError && idle) setSourceNote(gpsError);
      return;
    }
    setOrigin(coords);
    const how =
      coords.source === "ip" ? "ubicación aproximada por red" : `GPS en vivo (±${Math.round(coords.accuracyMeters || 0)} m)`;
    if (idle) setSourceNote(`Ubicación lista (${how}). Ajusta los filtros y pulsa «Buscar planes».`);
  }, [coords, gpsReady, gpsError, locationMode, origin, lastSearchKey]);

  const current = moodFromSlider(mood);

  // Sitios de la última búsqueda, ya filtrados con los filtros que había al pulsar «Buscar planes».
  const visiblePlaces = places;

  const selectedPlans = useMemo(
    () => plans.filter((plan) => selected[plan.title] && plan.map),
    [plans, selected],
  );

  const togglePlan = (title: string) => {
    setSelected((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const run = async () => {
    setFormError("");
    setRouteNote("");
    if (!time.ok) {
      setFormError(time.error);
      return;
    }
    if (!budget.ok) {
      setFormError(budget.error);
      return;
    }
    const point = locationMode === "manual" ? origin : coords || origin;
    if (!point) {
      setFormError(
        locationMode === "manual"
          ? "Primero sitúa la dirección o mueve el pin."
          : gpsError || "No pude leer la ubicación. Permite ubicación en el candado del navegador.",
      );
      return;
    }
    const freeMinutes = time.minutes;
    const maxBudgetEur = budget.budget;
    const searchKey = currentKey;
    const gen = ++runGen.current;
    setLoading(true);
    setAiNote("");
    setSourceNote("Buscando sitios cercanos…");
    try {
      const kit = await readKit();
      const radio = computeRadioMaximoKm(mode, freeMinutes / 60);
      const prev = lastFetch.current;
      let pool: NearbyPlace[];
      let radiusMeters = radio.radioMaximoMeters;
      if (prev && prev.radius === radio.radioMaximoMeters && coordsMovedMeters(prev, point) < 120) {
        pool = prev.places;
      } else {
        const fetched = await fetchNearbyPlacesCascade({ ...point, radii: engineRadii(radio.radioMaximoMeters) });
        pool = fetched.places;
        radiusMeters = fetched.radiusUsed || radio.radioMaximoMeters;
        lastFetch.current = { latitude: point.latitude, longitude: point.longitude, radius: radio.radioMaximoMeters, places: pool };
      }
      if (gen !== runGen.current) return;
      setRadiusUsed(radiusMeters);
      void fetchWeather(point.latitude, point.longitude)
        .then((w) => {
          if (w?.current?.temperature_2m != null) {
            setWeatherLine(`${Math.round(w.current.temperature_2m)}° · lluvia ${w.current.precipitation ?? 0} mm`);
          }
        })
        .catch(() => undefined);
      const fitting = placesForPlan(filterPlacesByCategories(pool, categories), { social, setting, freeOnly: maxBudgetEur === 0 }).filter(
        (p) => fitsBudget(p, maxBudgetEur) && fitsTime(p, freeMinutes, mode),
      );
      const engine = await searchPlansEngine({
        lat: point.latitude,
        lng: point.longitude,
        transporte: mode,
        minutes: freeMinutes,
        setting,
        categories,
        maxBudgetEur,
        social,
        mood,
        kit,
        places: pool,
      });
      if (gen !== runGen.current) return;
      let next = rankedItemsToPlanCards(engine.items, freeMinutes);
      if (next.length < 8) {
        const smart = await recommendPlansSmartDetailed({
          mood,
          social,
          places: fitting,
          kit,
          maxBudgetEur,
          setting,
          hour: new Date().getHours(),
          minutes: freeMinutes,
          transporte: mode,
          origin: point,
        });
        if (gen !== runGen.current) return;
        next = [...next, ...smart.cards.filter((p) => !next.some((n) => n.title === p.title))].slice(0, 20);
        if (categories.length) next = next.filter((p) => p.map);
        if (smart.note) setAiNote(smart.note);
      }
      setPlans(next);
      setPlaces(fitting);
      setSelected({});
      setLastSearchKey(searchKey);
      const discarded = engine.discardedByBudget + engine.discardedByTime;
      setSourceNote(
        next.length
          ? `${next.length} planes para ${formatMinutes(freeMinutes)} · presupuesto ${formatBudget(maxBudgetEur)} · radio ${engine.radioMaximoKm.toFixed(1)} km`
          : pool.length
            ? categories.length && engine.afterFilters === 0
              ? `No hay sitios de «${describeCategories(categories)}» en tu radio (${Object.entries(countByCategory(pool, categories)).map(([id, n]) => `${categoryById(id as never).label}: ${n}`).join(", ")}). Prueba con coche, más tiempo u otras categorías.`
              : `Ningún sitio encaja con ${formatMinutes(freeMinutes)} y presupuesto ${formatBudget(maxBudgetEur)}${discarded ? ` (${discarded} descartados)` : ""}. Prueba con más tiempo, más presupuesto u otro transporte.`
            : "No encontré sitios alrededor. Amplía el tiempo o cambia a coche.",
      );
    } catch {
      if (gen === runGen.current) {
        setSourceNote("No pude buscar ahora (sin conexión o servicio caído). Tus resultados anteriores siguen abajo; reintenta en unos segundos.");
      }
    } finally {
      if (gen === runGen.current) setLoading(false);
    }
  };

  const mapHtml = useMemo(() => {
    if (!origin) return "";
    const stops = selectedPlans.map((plan) => ({
      latitude: plan.map!.lat,
      longitude: plan.map!.lon,
      name: plan.placeName || plan.title,
    }));
    const nearby = visiblePlaces.slice(0, 30).map((place) => ({
      latitude: place.latitude,
      longitude: place.longitude,
      name: `${place.name} · ${PLACE_KIND_LABELS[place.kind] || place.kind}`,
    }));
    return leafletMapHtml(origin, origin, stops, nearby);
  }, [origin, selectedPlans, visiblePlaces]);

  const routeHref =
    origin && selectedPlans.length
      ? googleMapsLoop(
          origin,
          selectedPlans.map((plan) => ({
            latitude: plan.map!.lat,
            longitude: plan.map!.lon,
          })),
          mode,
        )
      : null;

  return (
    <div className="page-planes">
      <section className="planes-hero">
        <p className="eyebrow">A LA CALLE</p>
        <h1 className="display-brand">
          <span>HERMES</span>
        </h1>
        <p className="hero-line">
          GPS en vivo, sitios de alrededor y planes que encajan con cómo vas, el tiempo y la compañía.
        </p>
        <p className="hero-meta">
          {weatherLine ? `${weatherLine}. ` : ""}
          {sourceNote}
        </p>
        <div className="hero-cta-row">
          <button type="button" className={locationMode === "gps" ? "btn-copper" : "btn-ink"} onClick={() => setLocationMode("gps")}>
            Ubicación actual
          </button>
          <button type="button" className={locationMode === "manual" ? "btn-copper" : "btn-ink"} onClick={() => setLocationMode("manual")}>
            Ubicación manual
          </button>
        </div>
        {locationMode === "manual" ? (
          <form
            className="add-project"
            onSubmit={(event) => {
              event.preventDefault();
              const query = [address.street, address.number, address.postal, address.city, address.region, address.country]
                .map((part) => part.trim())
                .filter(Boolean)
                .join(", ");
              void geocodeAddress(query).then((hit) => {
                if (!hit) {
                  setGeoNote("No encontré esa dirección. Prueba con ciudad y calle, o mueve el pin.");
                  return;
                }
                setOrigin(hit);
                setGeoNote("Dirección situada en el mapa.");
              });
            }}
          >
            {(
              [
                ["country", "País"],
                ["region", "Provincia"],
                ["city", "Población"],
                ["postal", "Código postal"],
                ["street", "Calle"],
                ["number", "Número"],
              ] as const
            ).map(([key, label]) => (
              <label key={key} style={{ display: "grid", gap: 4, marginBottom: 8 }}>
                {label}
                <input
                  value={address[key]}
                  onChange={(event) => setAddress((current) => ({ ...current, [key]: event.target.value }))}
                />
              </label>
            ))}
            <button className="btn-copper" type="submit">Buscar esta dirección</button>
            <p className="hero-meta">{geoNote}</p>
            <iframe
              title="Elegir punto"
              style={{ width: "100%", height: 240, border: 0, borderRadius: 12 }}
              srcDoc={leafletPinPickerHtml(origin?.latitude ?? coords?.latitude ?? 40.4168, origin?.longitude ?? coords?.longitude ?? -3.7038)}
            />
          </form>
        ) : null}
      </section>

      {origin ? (
        <div className="map-frame">
          <iframe
            key={`${origin.latitude.toFixed(4)},${origin.longitude.toFixed(4)},${visiblePlaces.length},${selectedPlans.length}`}
            title="Sitios cerca"
            srcDoc={mapHtml}
            sandbox="allow-scripts allow-same-origin"
            allow="geolocation"
            loading="eager"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      ) : (
        <div className="map-frame">
          <div className="map-waiting">
            <p>Esperando tu GPS en vivo…</p>
            <p className="muted">{gpsError || "Permite la ubicación en el candado del navegador."}</p>
          </div>
        </div>
      )}

      {visiblePlaces.length ? (
        <div className="place-strip" aria-label="Sitios cercanos">
          {visiblePlaces.slice(0, 16).map((place) => (
            <span key={place.id} className="place-pill">
              <strong>{place.name}</strong>
              <span>
                {PLACE_KIND_LABELS[place.kind] || place.kind}
                {place.distanceMeters != null
                  ? ` · ${place.distanceMeters < 1000 ? `${place.distanceMeters} m` : `${(place.distanceMeters / 1000).toFixed(1)} km`}`
                  : ""}
              </span>
            </span>
          ))}
        </div>
      ) : null}

      <section className="control-strip" aria-label="Filtros">
        <div className="mood-block">
          <div className="mood-head">
            <span className="mood-emoji" aria-hidden>
              {current.emoji}
            </span>
            <div>
              <strong>{current.label}</strong>
              <p>{current.hint}</p>
            </div>
          </div>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={mood}
            onChange={(e) => setMood(Number(e.target.value))}
            aria-label="Ánimo"
            className="mood-range"
          />
          <div className="mood-ticks">
            {MOODS.map((m) => (
              <span key={m.id} title={m.label}>
                {m.emoji}
              </span>
            ))}
          </div>
        </div>

        <div className="social-grid">
          {SOCIAL_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`social-chip${social === opt.key ? " on" : ""}`}
              onClick={() => setSocial(opt.key)}
            >
              <span aria-hidden>{opt.icon}</span>
              {opt.label}
            </button>
          ))}
        </div>

        <CategoryFilter selected={categories} onChange={setCategories} />

        <PlanLimitsInputs value={limits} onChange={setLimits} idPrefix="planes" />

        <p className="label">Cómo vas (radio de búsqueda)</p>
        <div className="time-row">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              className={`time-chip${mode === m.key ? " on" : ""}`}
              onClick={() => setMode(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="toggle-row">
          <label>
            <input
              type="radio"
              name="setting"
              checked={setting === "any"}
              onChange={() => setSetting("any")}
            />
            Da igual
          </label>
          <label>
            <input
              type="radio"
              name="setting"
              checked={setting === "indoor"}
              onChange={() => setSetting("indoor")}
            />
            Prefiero interiores
          </label>
          <label>
            <input
              type="radio"
              name="setting"
              checked={setting === "outdoor"}
              onChange={() => setSetting("outdoor")}
            />
            Prefiero exteriores
          </label>
        </div>

        <div className="toggle-row">
          <button type="button" className="btn-copper" onClick={() => void run()} disabled={loading}>
            {loading ? "Mirando la zona…" : "Buscar planes"}
          </button>
          <button
            type="button"
            className="btn-ink"
            disabled={!selectedPlans.length || !origin}
            onClick={() =>
              setRouteNote(
                selectedPlans.length
                  ? `Ruta con ${selectedPlans.length} parada${selectedPlans.length === 1 ? "" : "s"} (vuelve al inicio).`
                  : "Marca planes con ubicación para trazar.",
              )
            }
          >
            Trazar ruta ({selectedPlans.length})
          </button>
          <button
            type="button"
            className="btn-ink"
            disabled={!selectedPlans.length}
            onClick={() => {
              void (async () => {
                try {
                  setSaveNote("Guardando…");
                  await savePlans(
                    selectedPlans.map((plan) => ({
                      title: plan.title,
                      detail: plan.detail,
                      minutes: plan.minutes,
                      placeName: plan.title,
                      map: plan.map || null,
                      price: plan.price,
                    })),
                  );
                  setSavedPlans(await readSavedPlans());
                  setSaveNote(`Guardados ${selectedPlans.length} plan(es) en tu cuenta.`);
                } catch (err) {
                  setSaveNote(err instanceof Error ? err.message : "No se pudieron guardar");
                }
              })();
            }}
          >
            Guardar selección
          </button>
        </div>
        {formError ? (
          <p className="limits-error" role="alert">
            {formError}
          </p>
        ) : null}
        {filtersChanged ? (
          <p className="filters-changed">Has cambiado los filtros. Pulsa «Buscar planes» para actualizar los resultados.</p>
        ) : null}
        {aiNote ? <p className="hero-meta">{aiNote}</p> : null}
        {routeNote ? <p className="hero-meta">{routeNote}</p> : null}
        {saveNote ? <p className="hero-meta">{saveNote}</p> : null}
        {routeHref ? (
          <a className="btn-copper map-open" href={routeHref} target="_blank" rel="noreferrer">
            Abrir ruta en Google Maps
          </a>
        ) : null}
      </section>

      {savedPlans.length ? (
        <section className="plan-list">
          <header className="list-head">
            <h2>Guardados en tu cuenta</h2>
            <span>{savedPlans.length}</span>
          </header>
          <ul>
            {savedPlans.map((plan) => (
              <li key={String(plan.id || plan.title)}>
                <div className="plan-row">
                  <div>
                    <h3>{plan.title}</h3>
                    <p>{plan.detail}</p>
                    <div className="plan-meta">
                      {plan.minutes != null ? <span>{plan.minutes} min</span> : null}
                      {plan.placeName ? <span>{plan.placeName}</span> : null}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="plan-list" aria-live="polite">
        <header className="list-head">
          <h2>Salidas</h2>
          <span>
            {plans.length} propuestas · {visiblePlaces.length} sitios
            {radiusUsed ? ` · ${(radiusUsed / 1000).toFixed(1)} km` : ""}
          </span>
        </header>
        {plans.length === 0 && !loading ? (
          <p className="empty">
            {lastSearchKey == null
              ? "Elige tiempo, presupuesto y compañía y pulsa «Buscar planes»."
              : "Ningún plan encaja con estos filtros. Prueba con más tiempo o presupuesto."}
          </p>
        ) : null}
        <ul>
          {plans.map((plan) => {
            const href = planMapsUrl(origin, plan, mode) || "#";
            const mappable = Boolean(plan.map);
            return (
              <li key={plan.title}>
                <div className={`plan-row${selected[plan.title] ? " plan-row-on" : ""}`}>
                  {mappable ? (
                    <label className="plan-select">
                      <input
                        type="checkbox"
                        checked={Boolean(selected[plan.title])}
                        onChange={() => togglePlan(plan.title)}
                      />
                      Ruta
                    </label>
                  ) : (
                    <span className="plan-select muted">Local</span>
                  )}
                  <a href={mappable ? href : undefined} target={mappable ? "_blank" : undefined} rel="noreferrer">
                    <h3>{plan.title}</h3>
                    <p>{plan.detail}</p>
                    <div className="plan-meta">
                      <span>{plan.minutes} min</span>
                      {plan.price != null ? <span>{plan.price > 0 ? `~${plan.price} €` : "Gratis"}</span> : null}
                      {mappable ? <span>Abrir en Maps</span> : <span>Sin mapa</span>}
                    </div>
                  </a>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
